import React, { useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { Upload, X, FileText, Check, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Modal para importar horas desde un CSV/XLSX exportado por un reloj marcador.
 *
 * Formato esperado (puede variar un poco; detectamos la fila de headers):
 *   ID de persona | Nombre de la persona | Fecha | Día de la semana | Primera perforación | Última perforación | ...
 *
 * - Admite .csv y .xlsx (incluso .csv que es realmente xlsx renombrado, usamos sniff).
 * - Empareja al trabajador 1) por cédula normalizada 2) por nombre normalizado.
 * - Importa TODAS las fechas presentes en el archivo (no solo la fecha visible).
 * - Sobrescribe cualquier registro existente para esa fecha + marca estado "Presente".
 */

const stripAccents = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const normName = (s) => stripAccents(s || "").toLowerCase().replace(/\s+/g, " ").trim();
const normCedula = (s) => (s || "").toString().replace(/[^0-9]/g, "").replace(/^0+/, "");

const HEADER_ALIASES = {
  cedula: ["id de persona", "cedula", "cédula", "identificacion", "identificación", "numero de identificacion", "número de identificación", "dni", "id"],
  nombre: ["nombre de la persona", "nombre", "nombre completo", "trabajador", "empleado", "persona"],
  fecha: ["fecha", "date", "dia", "día"],
  entrada: ["primera perforacion", "primera perforación", "entrada", "hora entrada", "hora de entrada", "primera marca", "inicio"],
  salida: ["ultima perforacion", "última perforación", "salida", "hora salida", "hora de salida", "ultima marca", "última marca", "fin"],
};

/** Dado el array raw (array de arrays), detecta la fila que contiene los headers y devuelve
 *  { headerRow, map } donde map es { cedula: idx, nombre: idx, fecha: idx, entrada: idx, salida: idx }
 */
function detectHeaders(rows) {
  for (let r = 0; r < Math.min(rows.length, 10); r++) {
    const row = rows[r] || [];
    const normalized = row.map(c => normName(c));
    const map = {};
    Object.entries(HEADER_ALIASES).forEach(([key, aliases]) => {
      for (let i = 0; i < normalized.length; i++) {
        if (aliases.includes(normalized[i])) { map[key] = i; break; }
      }
    });
    // Requerimos al menos nombre+fecha+entrada+salida; cedula es muy recomendable
    if (map.nombre != null && map.fecha != null && map.entrada != null && map.salida != null) {
      return { headerRow: r, map };
    }
  }
  return null;
}

/** Convierte un valor de fecha de Excel/XLSX a "YYYY-MM-DD".
 *  Nota: XLSX con cellDates:true construye los Date en hora LOCAL del navegador,
 *  por eso usamos getFullYear/getMonth/getDate (NO los UTC).
 */
function toISODate(v) {
  if (!v && v !== 0) return "";
  if (v instanceof Date) {
    const y = v.getFullYear();
    const m = String(v.getMonth() + 1).padStart(2, "0");
    const d = String(v.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  if (typeof v === "number") {
    // Serial de Excel
    const parsed = XLSX.SSF.parse_date_code(v);
    if (parsed) return `${parsed.y}-${String(parsed.m).padStart(2, "0")}-${String(parsed.d).padStart(2, "0")}`;
  }
  const s = String(v).trim();
  // ISO (2026-10-05 / 2026-10-05T00:00:00.000Z): usar el prefijo YYYY-MM-DD tal cual
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
  if (m) {
    // Asumimos DD/MM/YYYY (Costa Rica) salvo que el primer número sea > 12 → DD; o segundo > 12 → es MM/DD
    const a = parseInt(m[1], 10), b = parseInt(m[2], 10);
    let dd, mm;
    if (a > 12) { dd = a; mm = b; }
    else if (b > 12) { mm = a; dd = b; }
    else { dd = a; mm = b; } // default DD/MM
    return `${m[3]}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
  }
  return "";
}

/** Convierte un valor de hora de Excel/XLSX a "HH:MM".
 *  Nota: XLSX con cellDates:true construye los Date en hora LOCAL,
 *  por eso usamos getHours/getMinutes (NO los UTC).
 */
function toHHMM(v) {
  if (v == null || v === "") return "";
  if (v instanceof Date) {
    const hh = String(v.getHours()).padStart(2, "0");
    const mm = String(v.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  }
  if (typeof v === "number") {
    // XLSX puede traer la hora como fracción del día
    const totalMin = Math.round(v * 24 * 60);
    const hh = String(Math.floor(totalMin / 60) % 24).padStart(2, "0");
    const mm = String(totalMin % 60).padStart(2, "0");
    return `${hh}:${mm}`;
  }
  const s = String(v).trim();
  // "HH:MM" o "H:MM" (del parseo raw:false)
  const m = s.match(/(\d{1,2}):(\d{2})/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  return "";
}

function parseWorkbook(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        // cellDates:true → convierte fechas/horas a Date
        const wb = XLSX.read(data, { type: "array", cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, dateNF: "yyyy-mm-dd" });
        // Para fechas/horas necesitamos los raw Date objects: re-leer con raw:true
        const rowsRaw = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true });
        resolve({ rowsStr: rows, rowsRaw });
      } catch (err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

export default function AdjuntarCsvRelojModal({ empleados, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [parsed, setParsed] = useState(null); // { rows: [{cedula,nombre,fecha,entrada,salida,empleado,status}], unmatched, total }
  const inputRef = useRef(null);

  const indexByCedula = useMemo(() => {
    const m = new Map();
    (empleados || []).forEach(e => {
      const k = normCedula(e.cedula);
      if (k) m.set(k, e);
    });
    return m;
  }, [empleados]);

  const indexByName = useMemo(() => {
    const m = new Map();
    (empleados || []).forEach(e => {
      const full = normName(`${e.nombre || ""} ${e.apellidos || ""}`);
      if (full) m.set(full, e);
    });
    return m;
  }, [empleados]);

  const findEmpleado = (cedulaRaw, nombreRaw) => {
    const ced = normCedula(cedulaRaw);
    if (ced && indexByCedula.has(ced)) return indexByCedula.get(ced);
    const nm = normName(nombreRaw);
    if (nm && indexByName.has(nm)) return indexByName.get(nm);
    // Match tolerante: comparar sin orden (apellidos primero vs nombres primero)
    if (nm) {
      const partsA = nm.split(" ").filter(Boolean).sort().join(" ");
      for (const [key, emp] of indexByName.entries()) {
        const partsB = key.split(" ").filter(Boolean).sort().join(" ");
        if (partsA && partsA === partsB) return emp;
      }
    }
    return null;
  };

  const onPick = async (f) => {
    if (!f) return;
    setFile(f);
    setParsing(true);
    try {
      const { rowsStr, rowsRaw } = await parseWorkbook(f);
      const headers = detectHeaders(rowsStr);
      if (!headers) {
        toast.error("No se detectaron las columnas esperadas (ID, Nombre, Fecha, Primera perforación, Última perforación).");
        setParsed({ rows: [], unmatched: 0, matched: 0, total: 0 });
        setParsing(false);
        return;
      }
      const { headerRow, map } = headers;
      const dataRows = rowsRaw.slice(headerRow + 1);
      const out = [];
      let matched = 0, unmatched = 0;
      dataRows.forEach(row => {
        if (!row || row.every(c => c == null || c === "")) return;
        const cedula = row[map.cedula] != null ? row[map.cedula] : "";
        const nombre = row[map.nombre] != null ? row[map.nombre] : "";
        const fecha = toISODate(row[map.fecha]);
        const entrada = toHHMM(row[map.entrada]);
        const salida = toHHMM(row[map.salida]);
        if (!fecha || !entrada || !salida) return;
        const emp = findEmpleado(cedula, nombre);
        if (emp) matched++; else unmatched++;
        out.push({
          cedula: String(cedula || ""),
          nombre: String(nombre || ""),
          fecha, entrada, salida,
          empleado: emp,
        });
      });
      setParsed({ rows: out, matched, unmatched, total: out.length });
    } catch (e) {
      toast.error("No se pudo leer el archivo: " + (e.message || ""));
      setParsed(null);
    } finally {
      setParsing(false);
    }
  };

  const importar = async () => {
    if (!parsed || !parsed.rows.length) return;
    const toImport = parsed.rows.filter(r => r.empleado);
    if (!toImport.length) {
      toast.error("No hay filas emparejadas con empleados activos.");
      return;
    }
    setImporting(true);
    setProgress({ done: 0, total: toImport.length });
    let ok = 0, fail = 0;
    for (let i = 0; i < toImport.length; i++) {
      const r = toImport[i];
      try {
        await api.post("/asistencia", {
          empleado_id: r.empleado.id,
          fecha: r.fecha,
          estado: "Presente",
          entrada: r.entrada,
          salida: r.salida,
        });
        ok++;
      } catch (e) {
        fail++;
      }
      setProgress({ done: i + 1, total: toImport.length });
    }
    setImporting(false);
    if (ok > 0) toast.success(`Se importaron ${ok} registros${fail ? ` (${fail} con error)` : ""}.`);
    if (ok === 0 && fail > 0) toast.error("No se pudo importar ningún registro.");
    if (onImported) onImported({ ok, fail });
    if (ok > 0 && fail === 0) onClose?.();
  };

  const triggerPick = () => inputRef.current?.click();

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" data-testid="reloj-csv-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-c">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E6EFEA] text-primary-color flex items-center justify-center">
              <Upload size={18} />
            </div>
            <div>
              <h2 className="font-display text-xl font-semibold">Adjuntar CSV de reloj marcador</h2>
              <p className="text-xs text-secondary-c">Importa horas de entrada y salida desde un archivo .csv o .xlsx</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#FAFAF7]" data-testid="reloj-csv-close"><X size={18} /></button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {!parsed && (
            <div
              onClick={triggerPick}
              onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={e => {
                e.preventDefault(); e.stopPropagation();
                const f = e.dataTransfer?.files?.[0]; if (f) onPick(f);
              }}
              className="border-2 border-dashed border-c rounded-2xl p-10 text-center cursor-pointer hover:bg-[#FAFAF7]"
              data-testid="reloj-csv-dropzone"
            >
              <FileText size={36} className="mx-auto text-secondary-c mb-3" />
              <div className="font-medium">Arrastrá el archivo aquí o hacé clic para seleccionarlo</div>
              <div className="text-xs text-secondary-c mt-1">Formatos aceptados: .csv, .xlsx</div>
              {parsing && <div className="mt-3 inline-flex items-center gap-2 text-sm text-secondary-c"><Loader2 size={14} className="animate-spin" /> Procesando archivo…</div>}
            </div>
          )}

          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            className="hidden"
            onChange={e => onPick(e.target.files?.[0])}
            data-testid="reloj-csv-input"
          />

          {parsed && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap text-sm">
                <div className="np-badge np-badge-info"><FileText size={12} className="mr-1" />{file?.name}</div>
                <div className="np-badge np-badge-success">Emparejados: {parsed.matched}</div>
                {parsed.unmatched > 0 && <div className="np-badge np-badge-warning"><AlertTriangle size={12} className="mr-1" />Sin emparejar: {parsed.unmatched}</div>}
                <div className="np-badge np-badge-neutral">Total filas: {parsed.total}</div>
                <button
                  type="button"
                  onClick={() => { setParsed(null); setFile(null); }}
                  className="np-btn np-btn-outline py-1 px-2 text-xs ml-auto"
                  data-testid="reloj-csv-reset"
                >Cambiar archivo</button>
              </div>

              {parsed.rows.length === 0 && (
                <div className="text-sm text-secondary-c italic text-center py-6 rounded-xl border border-dashed border-c">
                  No se detectaron filas válidas en el archivo.
                </div>
              )}

              {parsed.rows.length > 0 && (
                <div className="np-scroll max-h-[42vh] rounded-xl border border-c" data-testid="reloj-csv-preview">
                  <table className="np-table">
                    <thead>
                      <tr>
                        <th>Cédula</th>
                        <th>Nombre (archivo)</th>
                        <th>Fecha</th>
                        <th>Entrada</th>
                        <th>Salida</th>
                        <th>Empleado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.rows.map((r, i) => (
                        <tr key={i}>
                          <td className="text-xs">{r.cedula || "—"}</td>
                          <td className="text-xs">{r.nombre || "—"}</td>
                          <td className="text-xs">{r.fecha}</td>
                          <td className="text-xs">{r.entrada}</td>
                          <td className="text-xs">{r.salida}</td>
                          <td className="text-xs">
                            {r.empleado
                              ? <span className="inline-flex items-center gap-1 text-success"><Check size={12} />{r.empleado.nombre} {r.empleado.apellidos}</span>
                              : <span className="inline-flex items-center gap-1 text-warning"><AlertTriangle size={12} />Sin match</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="text-xs text-secondary-c leading-relaxed">
                <strong>Nota:</strong> al importar, cada registro se guarda con estado <strong>"Presente"</strong> y <strong>sobrescribe</strong> cualquier dato previo para la misma fecha. Las filas sin empleado emparejado se omiten.
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-c flex items-center justify-end gap-2 bg-[#FAFAF7]">
          {importing && (
            <div className="text-xs text-secondary-c mr-auto inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Importando {progress.done} / {progress.total}…
            </div>
          )}
          <button onClick={onClose} disabled={importing} className="np-btn np-btn-outline" data-testid="reloj-csv-cancel">Cancelar</button>
          <button
            onClick={importar}
            disabled={!parsed || parsed.matched === 0 || importing}
            className="np-btn np-btn-primary"
            data-testid="reloj-csv-import"
          >
            <Upload size={14} /> Importar {parsed?.matched ? `${parsed.matched} registros` : ""}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
